'use client';
import {
    ContentHeaderContainer,
    ContentHeaderTitleBig,
    ContentHeaderTitleMedium,
    ContentHeaderTitleSmall
} from './styles';
import { BoxProps } from '@mui/material';
import { TextButton } from '../buttons';
import { ArrowBack } from '@mui/icons-material';
import { useRouter } from 'next/navigation';
import { Column, HeadlineSmall, Row, TitleMedium } from '../../styles';
import { useBreakpoint, useTheme, useToggle } from '../../hooks';
import { isCompact, isMedium } from '../../types';
import { Fragment, ReactNode, useEffect } from 'react';

interface ContentHeaderProps extends BoxProps {
    title: string;
    hasBackButton?: boolean;
    backButtonLabel?: string;
    goBackWarning?: string;
    additionalElements?: ReactNode[];
    type?: HeaderType;
    isSticky?: boolean;
    onGoBack?: () => void;
    description?: string;
}

export enum HeaderType {
    MAIN_PAGE,
    SUB_PAGE
}

/**
 * General purpose Header for content pages
 */
export function ContentHeader({
    title,
    hasBackButton,
    backButtonLabel,
    additionalElements,
    type = HeaderType.MAIN_PAGE,
    isSticky = false,
    goBackWarning,
    onGoBack,
    description,
    ...boxProps
}: ContentHeaderProps) {
    const router = useRouter();
    const breakpoint = useBreakpoint();
    const { colors } = useTheme();
    const [isScrolled, setScrolled] = useToggle();

    useEffect(() => {
        const handleScroll = () => {
            const scrollableHeight = document.documentElement.scrollHeight - window.innerHeight;
            if (scrollableHeight >= 80 && window.scrollY > 40) {
                setScrolled(true);
            } else if (window.scrollY < 10) {
                setScrolled(false);
            }
        };
        if (isSticky) {
            window.window.addEventListener('scroll', handleScroll, { passive: true });
            return () => {
                window.removeEventListener('scroll', handleScroll);
            };
        }
    }, [isSticky, setScrolled]);

    const renderTitle = () => {
        if (isCompact(breakpoint)) {
            return <ContentHeaderTitleSmall type={type}>{title}</ContentHeaderTitleSmall>;
        } else if (isMedium(breakpoint) || type === HeaderType.SUB_PAGE || isScrolled) {
            return <ContentHeaderTitleMedium type={type}>{title}</ContentHeaderTitleMedium>;
        } else {
            return <ContentHeaderTitleBig type={type}>{title}</ContentHeaderTitleBig>;
        }
    };

    const renderDescription = () => {
        if (description) {
            if (isCompact(breakpoint)) {
                return <TitleMedium color={colors.primaryContainerDark}>{description}</TitleMedium>;
            } else {
                return (
                    <HeadlineSmall color={colors.primaryContainerDark}>{description}</HeadlineSmall>
                );
            }
        }
        return null;
    };

    const onPressBack = () => {
        const shouldGoBack = goBackWarning !== undefined ? confirm(goBackWarning) : true;

        if (shouldGoBack) {
            onGoBack ? onGoBack() : router.back();
        }
    };

    return (
        <ContentHeaderContainer
            breakpoint={breakpoint}
            headerType={type}
            isScrolled={isScrolled}
            {...boxProps}
        >
            {hasBackButton && backButtonLabel && (
                <TextButton
                    label={backButtonLabel}
                    onClick={onPressBack}
                    icon={<ArrowBack />}
                    color='primary'
                />
            )}
            <Row stretch width='100%' sx={{ flexWrap: 'wrap' }} gap={0.5}>
                <Column gap={1}>
                    {renderTitle()}
                    {renderDescription()}
                </Column>
                {additionalElements?.map((element, index) => (
                    <Fragment key={index}>{element}</Fragment>
                ))}
            </Row>
        </ContentHeaderContainer>
    );
}
